// OWNER: LG-ADMIN. §10 tab 5 (gold sets, §7.3): pick niche and detector, then one item at a time: the detector's claim,
// the evidence link, the screenshot or review quote, the Maps link. Correct / Wrong / Unsure (keys 1, 2, 3). Precision
// is shown live against the 0.95 (opener) and 0.85 (scoring) bars. Below it, the §14 calibration report for the niche.
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { CalibrationReport, CheckItem, CheckVerdict, DetectorEval, NicheProfileRow } from '../../../src/shared/api-leadgen';
import { Empty, ErrorBanner, fmtDateTime, toast } from '../components/ui';
import { ApiErrorBox, Field, inputValue, useLoad } from '../admin/ui';
import { apiErrorView, type ApiErrorView } from '../admin/errors';
import { leadgenApi } from './endpoints';
import {
  ACTIVATION_GATE_LABELS, MIN_CHECKED, OPENER_MIN_PRECISION, SCORING_MIN_PRECISION, TIER_TONES, evalStanding, gatesFromError, inNicheActivateReady, int,
  isTypingTarget, mergeCheckItems, newestApproved, share, verdictForKey,
} from './logic';
import { ExtLink, Meter, ToneChip } from './parts';

const VERDICTS: Array<{ v: CheckVerdict; key: string; label: string; cls: string }> = [
  { v: 'correct', key: '1', label: 'Correct', cls: 'btn-primary' },
  { v: 'wrong', key: '2', label: 'Wrong', cls: 'btn-danger' },
  { v: 'unsure', key: '3', label: 'Unsure', cls: '' },
];

export function ChecksTab(_props: Record<string, never>) {
  const niches = useLoad(() => leadgenApi.niches(), []);
  const keys = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of niches.data ?? []) if (!m.has(n.key) || n.status === 'active') m.set(n.key, n.displayName);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [niches.data]);
  const [niche, setNiche] = useState('');
  useEffect(() => { if (!niche && keys.length) setNiche(keys[0][0]); }, [keys.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div class="stack" style="gap:14px">
      <div class="adm-toolbar">
        <Field label="Niche">
          <select value={niche} onChange={(e) => setNiche(inputValue(e))}>
            {keys.length === 0 ? <option value="">{niches.loading ? 'Loading…' : 'No niches yet'}</option> : null}
            {keys.map(([k, name]) => <option value={k}>{name} ({k})</option>)}
          </select>
        </Field>
        <span class="lg-note grow">A detector opens calls only at precision ≥ {OPENER_MIN_PRECISION} on n ≥ {MIN_CHECKED}; under {SCORING_MIN_PRECISION} it stops scoring.</span>
      </div>
      <ErrorBanner error={niches.error} />
      {niche ? <GoldSets key={niche} niche={niche} rows={niches.data ?? []} onActivated={() => void niches.reload()} /> : null}
      {niche ? <Calibration key={`cal-${niche}`} niche={niche} /> : null}
    </div>
  );
}

function GoldSets(props: { niche: string; rows: NicheProfileRow[]; onActivated: () => void }) {
  const evals = useLoad(() => leadgenApi.evals(props.niche), [props.niche]);
  const [detector, setDetector] = useState('');
  const [custom, setCustom] = useState('');
  const [item, setItem] = useState<CheckItem | null | undefined>(undefined);
  const [loadingItem, setLoadingItem] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiErrorView | null>(null);
  const [live, setLive] = useState<DetectorEval | null>(null);
  const noteRef = useRef<HTMLInputElement>(null);
  const [batch, setBatch] = useState(false);

  const detectors = useMemo(() => [...new Set((evals.data ?? []).map((e) => e.detector))].sort(), [evals.data]);
  const active = detector === '__other' ? custom.trim() : detector;
  useEffect(() => { if (!detector && detectors.length) setDetector(detectors[0]); }, [detectors.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const next = async () => {
    if (!active) return;
    setLoadingItem(true); setError(null);
    try { const r = await leadgenApi.nextCheck(props.niche, active); setItem(r.item); setNote(''); }
    catch (e) { setError(apiErrorView(e)); setItem(null); } finally { setLoadingItem(false); }
  };
  useEffect(() => { setItem(undefined); setLive(null); if (active) void next(); }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (verdict: CheckVerdict) => {
    if (!item || busy) return;
    setBusy(true); setError(null);
    try {
      const r = await leadgenApi.submitCheck({
        detector: item.detector, detectorVersion: item.detectorVersion, nicheKey: item.nicheKey, placeId: item.placeId,
        detectorSaid: item.detectorSaid, verdict, ...(note.trim() ? { note: note.trim() } : {}),
      });
      setLive(r.eval);
      evals.setData([...(evals.data ?? []).filter((e) => !(e.detector === r.eval.detector && e.detectorVersion === r.eval.detectorVersion)), r.eval]);
      await next();
    } catch (e) { setError(apiErrorView(e)); toast(apiErrorView(e).message, 'bad'); } finally { setBusy(false); }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const v = verdictForKey(e.key, { typing: isTypingTarget(t?.tagName, !!t?.isContentEditable), modifier: e.ctrlKey || e.metaKey || e.altKey });
      if (v && item && !batch) { e.preventDefault(); void submit(v); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const current = live ?? (evals.data ?? []).find((e) => e.detector === active) ?? null;
  const standing = current ? evalStanding(current) : null;

  return (
    <div class="lg-grid-2">
      <div class="card stack">
        <div class="lg-card-head">
          <h3>Gold-set check</h3>
          <div class="row">
            <select value={detector} onChange={(e) => setDetector(inputValue(e))} aria-label="Detector">
              {detectors.map((d) => <option value={d}>{d}</option>)}
              <option value="__other">Other detector…</option>
            </select>
            {detector === '__other' ? <input value={custom} placeholder="detector name" aria-label="Detector name" onInput={(e) => setCustom(inputValue(e))} style="width:160px" /> : null}
            {active === 'in_niche' ? (
              <button class={`btn btn-sm ${batch ? 'btn-ink' : ''}`} onClick={() => setBatch(!batch)}>{batch ? 'One at a time' : 'Check 30 at once'}</button>
            ) : null}
          </div>
        </div>
        {current && standing ? (
          <div class="stack" style="gap:4px">
            <div class="row" style="justify-content:space-between"><b class="lg-mono">{current.detector} v{current.detectorVersion}</b><ToneChip tone={standing.tone}>{standing.text}</ToneChip></div>
            <Meter value={current.precision} marks={[SCORING_MIN_PRECISION, OPENER_MIN_PRECISION]} tone={current.precision !== null && current.precision < SCORING_MIN_PRECISION ? 'over' : ''} label="Precision" />
            <div class="lg-meter-legend"><span>0</span><span>{SCORING_MIN_PRECISION} scoring · {OPENER_MIN_PRECISION} opener</span><span>1</span></div>
          </div>
        ) : null}
        <ApiErrorBox error={error} />
        {!active ? <div class="lg-note">Pick a detector. The list shows detectors the server has evals for in this niche.</div> : null}
        {active === 'in_niche' && batch ? (
          <InNicheBatch niche={props.niche} row={newestApproved(props.rows, props.niche)}
            onEval={(ev) => { setLive(ev); evals.setData([...(evals.data ?? []).filter((e) => !(e.detector === ev.detector && e.detectorVersion === ev.detectorVersion)), ev]); }}
            current={current} onActivated={props.onActivated} />
        ) : null}
        {batch && active === 'in_niche' ? null : <>
        {active && (loadingItem || item === undefined) ? <div class="muted">Loading the next item…</div> : null}
        {active && item === null && !loadingItem ? <Empty>Nothing left to check for {active} in this niche.</Empty> : null}
        {item ? (
          <div class="lg-check">
            <div class="row" style="justify-content:space-between;flex-wrap:wrap">
              <span><b>{item.name}</b>{item.city ? <span class="lg-note"> · {item.city}</span> : null}</span>
              <span class="lg-mono">positives {item.progress.positives}/{item.progress.targetPositives} · negatives {item.progress.negatives}/{item.progress.targetNegatives}</span>
            </div>
            <div class="eyebrow">The detector says ({item.detectorSaid})</div>
            <div class="lg-claim">{item.claim}</div>
            {item.quote ? <blockquote class="lg-quote">{item.quote}</blockquote> : null}
            {item.screenshotUrl ? <img class="lg-shot" src={item.screenshotUrl} alt={`Screenshot of ${item.name}'s website`} loading="lazy" /> : null}
            <div class="lg-links">
              <ExtLink href={item.evidenceUrl}>Open the evidence</ExtLink>
              <ExtLink href={item.mapsUrl}>Google Maps</ExtLink>
            </div>
            <Field label="Note (optional)"><input ref={noteRef} value={note} maxLength={300} onInput={(e) => setNote(inputValue(e))} placeholder="Why it's wrong or unsure" /></Field>
            <div class="lg-verdicts">
              {VERDICTS.map((x) => <button class={`btn ${x.cls}`} disabled={busy} onClick={() => void submit(x.v)}><kbd>{x.key}</kbd> {x.label}</button>)}
            </div>
            <span class="lg-note">Keys 1, 2 and 3 work unless you're typing in the note.</span>
          </div>
        ) : null}
        </>}
      </div>

      <div class="card stack">
        <h3 style="margin:0">Precision in this niche</h3>
        <ErrorBanner error={evals.error} />
        {evals.data && evals.data.length === 0 ? <div class="lg-note">No detector has checks here yet.</div> : null}
        {evals.data && evals.data.length ? (
          <div class="adm-table-wrap">
            <table>
              <thead><tr><th>Detector</th><th class="num">Positives ✓/✗/?</th><th class="num">Negatives ✓/✗/?</th><th class="num">n</th><th class="num">Precision</th><th>Standing</th></tr></thead>
              <tbody>
                {[...evals.data].sort((a, b) => a.detector.localeCompare(b.detector) || b.detectorVersion - a.detectorVersion).map((e) => {
                  const s = evalStanding(e);
                  return (
                    <tr class="adm-clickable" onClick={() => setDetector(e.detector)}>
                      <td class="lg-mono">{e.detector} v{e.detectorVersion}</td>
                      <td class="num">{e.positives.correct}/{e.positives.wrong}/{e.positives.unsure}</td>
                      <td class="num">{e.negatives.correct}/{e.negatives.wrong}/{e.negatives.unsure}</td>
                      <td class="num">{e.n}</td>
                      <td class="num">{e.precision === null ? '—' : e.precision.toFixed(2)}</td>
                      <td><ToneChip tone={s.tone}>{s.opener === 'eligible' ? 'opener' : s.scoring === 'off' ? 'off' : s.needMore ? `${s.needMore} to go` : 'mid-call'}</ToneChip></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** P0-6: up to 30 in-niche items in one table (GET /checks/batch), verdicts by click or keys (J/K move, 1/2/3 judge),
 *  the precision meter live, and Activate inline once every gate the page can see is met (the server re-checks all). */
function InNicheBatch(props: { niche: string; row: NicheProfileRow | null; current: DetectorEval | null; onEval: (ev: DetectorEval) => void; onActivated: () => void }) {
  const [items, setItems] = useState<CheckItem[]>([]);
  const [done, setDone] = useState<Record<string, CheckVerdict>>({});
  const [cursor, setCursor] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState<ApiErrorView | null>(null);
  const [blocked, setBlocked] = useState<ReturnType<typeof gatesFromError>>([]);

  const load = async () => {
    setLoading(true); setError(null);
    try { const r = await leadgenApi.checkBatch(props.niche, 30); setItems((have) => mergeCheckItems(have.filter((x) => !done[x.placeId]), r.items)); setCursor(0); }
    catch (e) { setError(apiErrorView(e)); } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [props.niche]); // eslint-disable-line react-hooks/exhaustive-deps

  const judge = async (it: CheckItem, verdict: CheckVerdict) => {
    if (busy || done[it.placeId]) return;
    setBusy(it.placeId); setError(null);
    try {
      const r = await leadgenApi.submitCheck({ detector: it.detector, detectorVersion: it.detectorVersion, nicheKey: it.nicheKey, placeId: it.placeId, detectorSaid: it.detectorSaid, verdict });
      setDone((d) => ({ ...d, [it.placeId]: verdict }));
      props.onEval(r.eval);
      const next = items.findIndex((x, i) => i > items.indexOf(it) && !done[x.placeId] && x.placeId !== it.placeId);
      if (next >= 0) setCursor(next);
    } catch (e) { setError(apiErrorView(e)); } finally { setBusy(''); }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (isTypingTarget(t?.tagName, !!t?.isContentEditable) || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'j' || e.key === 'J') { e.preventDefault(); setCursor((c) => Math.min(items.length - 1, c + 1)); return; }
      if (e.key === 'k' || e.key === 'K') { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); return; }
      const v = verdictForKey(e.key, { typing: false, modifier: false });
      const it = items[cursor];
      if (v && it) { e.preventDefault(); void judge(it, v); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const ready = inNicheActivateReady(props.current, props.row);
  const activate = async () => {
    if (!props.row) return;
    setBusy('activate'); setError(null); setBlocked([]);
    try { const r = await leadgenApi.activateNiche(props.row.key, props.row.version); toast(`${r.displayName} v${r.version} is active. Full searches are unlocked.`, 'ok'); props.onActivated(); }
    catch (e) { setBlocked(gatesFromError(e)); setError(apiErrorView(e)); } finally { setBusy(''); }
  };
  const judged = Object.keys(done).length;

  return (
    <div class="stack" style="gap:8px">
      <div class="row" style="justify-content:space-between;flex-wrap:wrap">
        <span class="lg-note">Is each place really a {props.row?.displayName ?? props.niche} business? {judged} judged here · keys J/K move, 1 correct, 2 wrong, 3 unsure.</span>
        <button class="btn btn-sm" onClick={() => void load()} disabled={loading}>{loading ? 'Loading…' : 'Load more'}</button>
      </div>
      <ApiErrorBox error={error} />
      {blocked.length ? <ul class="adm-problems">{blocked.filter((g) => !g.ok).map((g) => <li class="error">{ACTIVATION_GATE_LABELS[g.key] ?? g.key}: {g.detail}</li>)}</ul> : null}
      {ready && props.row ? (
        <div class="row" style="justify-content:space-between">
          <ToneChip tone="ok">In-niche precision meets the bar on {props.current?.n} checks</ToneChip>
          <button class="btn btn-primary" disabled={busy === 'activate'} onClick={() => void activate()}>Activate {props.row.displayName} v{props.row.version}</button>
        </div>
      ) : null}
      {!loading && items.length === 0 ? <Empty>Nothing left to check in this niche. Run a pilot to get more places.</Empty> : null}
      {items.length ? (
        <div class="adm-table-wrap">
          <table>
            <thead><tr><th>Place</th><th>Google label</th><th>City</th><th>Links</th><th>Verdict</th></tr></thead>
            <tbody>
              {items.map((it, i) => {
                const v = done[it.placeId];
                return (
                  <tr class={i === cursor ? 'lg-row-cursor' : v ? 'adm-dim' : ''} onClick={() => setCursor(i)}>
                    <td><b>{it.name}</b></td>
                    <td class="lg-note" style="white-space:normal">{it.claim.replace(/^.*\(label: (.*)\)\.$/, '$1')}</td>
                    <td>{it.city ?? '—'}</td>
                    <td><ExtLink href={it.mapsUrl}>Maps</ExtLink></td>
                    <td>{v ? <ToneChip tone={v === 'correct' ? 'ok' : v === 'wrong' ? 'bad' : ''}>{v}</ToneChip> : (
                      <div class="row" style="gap:4px">
                        {VERDICTS.map((x) => <button class={`btn btn-sm ${x.cls}`} disabled={!!busy} onClick={(e) => { e.stopPropagation(); void judge(it, x.v); }} title={`Key ${x.key}`}>{x.label}</button>)}
                      </div>
                    )}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function Calibration(props: { niche: string }) {
  const cal = useLoad<CalibrationReport>(() => leadgenApi.calibration(props.niche), [props.niche]);
  const c = cal.data;
  return (
    <div class="card stack">
      <div class="lg-card-head">
        <h3>Scoring calibration</h3>
        {c ? <span class="lg-note">weights {c.weightsVersion} · built {fmtDateTime(c.generatedAt)} (Mondays)</span> : null}
      </div>
      <ErrorBanner error={cal.error} />
      {cal.loading && !c ? <div class="muted">Loading…</div> : null}
      {c ? (
        <>
          <div class="lg-grid-2">
            <div class="stack">
              <span class="eyebrow">By tier</span>
              <div class="adm-table-wrap">
                <table>
                  <thead><tr><th>Tier</th><th class="num">Dials</th><th class="num">DM convos</th><th class="num">Meetings</th><th class="num">DM / 100 dials</th><th class="num">Mtgs / 100 DM</th><th class="num">Mtgs / 100 dials</th></tr></thead>
                  <tbody>{c.tiers.map((t) => (
                    <tr><td><ToneChip tone={TIER_TONES[t.tier]}>{t.tier}</ToneChip></td><td class="num">{int(t.dials)}</td><td class="num">{int(t.dmConversations)}</td><td class="num">{int(t.meetings)}</td>
                      <td class="num">{t.dmPer100Dials?.toFixed(1) ?? '—'}</td><td class="num">{t.meetingsPer100Dm?.toFixed(1) ?? '—'}</td><td class="num">{t.meetingsPer100Dials?.toFixed(2) ?? '—'}</td></tr>
                  ))}</tbody>
                </table>
              </div>
              <div class="lg-note">
                Kill rule: {c.killRule.evaluated
                  ? `A ${c.killRule.tierAPer100?.toFixed(2) ?? '—'} vs C ${c.killRule.tierCPer100?.toFixed(2) ?? '—'} meetings per 100 dials (ratio ${c.killRule.ratio?.toFixed(2) ?? '—'}; needs 1.5).${c.killRule.triggered ? ' Triggered: priority ordering is off.' : ''}`
                  : 'not evaluated yet (needs 600 dials per tier).'}
              </div>
            </div>
            <div class="stack">
              <span class="eyebrow">List health ({int(c.listHealth.dialled)} dialled)</span>
              <table>
                <tbody>
                  <tr><td>Wrong numbers</td><td class="num">{share(c.listHealth.wrongNumberRate, 1)}</td><td class="lg-note">target under 3%</td></tr>
                  <tr><td>Not a fit</td><td class="num">{share(c.listHealth.notAFitRate, 1)}</td><td class="lg-note">under 5%</td></tr>
                  <tr><td>Found closed</td><td class="num">{share(c.listHealth.closedRate, 1)}</td><td class="lg-note">under 1%</td></tr>
                </tbody>
              </table>
              {c.listHealth.breaches.length ? <ul class="adm-problems">{c.listHealth.breaches.map((b) => <li class="error">{b}</li>)}</ul> : <span class="lg-note">No breaches.</span>}
            </div>
          </div>
          <div class="stack">
            <span class="eyebrow">Signals: meetings per DM conversation, present vs absent (Wilson 90%)</span>
            {c.signals.length ? (
              <div class="adm-table-wrap">
                <table>
                  <thead><tr><th>Signal</th><th class="num">DM present / absent</th><th class="num">Meetings present / absent</th><th class="num">Lift</th><th class="num">90% interval</th><th>Proposal</th></tr></thead>
                  <tbody>{c.signals.map((s) => (
                    <tr><td class="lg-mono">{s.signal}</td><td class="num">{int(s.presentDm)} / {int(s.absentDm)}</td><td class="num">{int(s.presentMeetings)} / {int(s.absentMeetings)}</td>
                      <td class="num">{s.lift === null ? '—' : `${s.lift.toFixed(2)}×`}</td><td class="num">{s.ci90 ? `${s.ci90[0].toFixed(2)}–${s.ci90[1].toFixed(2)}` : '—'}</td>
                      <td>{s.proposal ? <ToneChip tone={s.proposal === 'raise' ? 'ok' : 'warn'}>{s.proposal === 'raise' ? 'raise by half' : 'halve'}</ToneChip> : <span class="muted">—</span>}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            ) : <span class="lg-note">No signal data yet.</span>}
            <span class="lg-note">Proposals are not applied automatically. Applying one means a new weights version, which re-scores open memberships.</span>
          </div>
          {c.objectionRateByFdBand.length ? (
            <div class="stack">
              <span class="eyebrow">“We have someone” objections by Get Answered fit band</span>
              <table><thead><tr><th>Band</th><th class="num">DM convos</th><th class="num">“Have someone”</th><th class="num">Rate</th></tr></thead>
                <tbody>{c.objectionRateByFdBand.map((b) => <tr><td>{b.band}</td><td class="num">{int(b.dmConversations)}</td><td class="num">{int(b.haveSomeone)}</td><td class="num">{share(b.rate, 1)}</td></tr>)}</tbody></table>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
